import React, { useEffect, useMemo } from 'react'
import { Select, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Header } from './styled'
import { Contents } from './layouts/styled'
import { TaskList, SelectButton, ModalAction, LoadMoreTasks } from './components'
import { TASK_STATUS_FILTERS, TASK_STATUS_FILTER_VALUES, TASK_CATEGORY_VALUE, DEFAULT_PAGE } from './constants'
import useQuery from '../../hooks/useQuery'
import { useProfile, useTaskManagement } from '../../hooks'

const { Option } = Select

const TaskScreen = ({
  items,
  setItems,
  isSelectedAll,
  isModalActionVisible,
  setIsModalActionVisible
}) => {
  const { t } = useTranslation()
  const query = useQuery()
  const taskTypeId = query.get('taskTypeId')
  const taskCategoryId = query.get('taskCategoryId')
  const { profile: { userId } } = useProfile()
  const {
    tasksData,
    loadTasksAction,
    isLoadingTasks,
    loadMoreTasksAction,
    isMoreLoadingTasks,
    updateStatusFilterAction,
    statusFilter,
    page,
    setPage
  } = useTaskManagement()
  const dataSource = useMemo(() => tasksData.items, [tasksData.items])

  useEffect(() => {
    setItems(
      dataSource
        .map((item) => ({ ...item, selected: false }))
        .filter((item) => {
          switch (statusFilter) {
            case TASK_STATUS_FILTER_VALUES.UNCOMPLETED:
              return item.statusName !== TASK_STATUS_FILTER_VALUES.COMPLETED
            case TASK_STATUS_FILTER_VALUES.COMPLETED:
              return item.statusName === TASK_STATUS_FILTER_VALUES.COMPLETED
            default:
              return true
          }
        })
    )
  }, [dataSource, statusFilter])

  useEffect(() => {
    loadTasksAction({
      taskTypeId: taskTypeId || TASK_CATEGORY_VALUE.MY_TASK,
      referenceId: taskCategoryId || userId
    })
  }, [taskTypeId, taskCategoryId])

  useEffect(() => {
    if (isLoadingTasks) {
      setItems([])
    }
  }, [isLoadingTasks])

  const handleSelectAll = () => {
    setItems(items.map((item) => ({ ...item, selected: item.isRequesterMe ? !isSelectedAll : false })))
  }

  const handleSelectTaskFilter = (value) => {
    updateStatusFilterAction(value)
  }

  const handleModalActionVisible = () => {
    setIsModalActionVisible((prev) => !prev)
  }

  return (
    <>
      <Contents>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, paddingLeft: 12.5 }}>
            <SelectButton selected={isSelectedAll} onClick={handleSelectAll} size={25} />
            {t('task.select_all')}
          </div>

          <Select onChange={handleSelectTaskFilter} style={{ width: 120 }} defaultValue={statusFilter}>
            {TASK_STATUS_FILTERS.map(({ key, value }) => (
              <Option key={key} value={value}>
                {t(`task.${value}`)}
              </Option>
            ))}
          </Select>
        </Header>

        <Spin spinning={isLoadingTasks} size="large">
          <TaskList t={t} items={items} setItems={setItems} />
        </Spin>
        {dataSource.length >= DEFAULT_PAGE.PER_PAGE && (
        <Spin spinning={isMoreLoadingTasks} size="large">
          <LoadMoreTasks tasksData={tasksData} loadMoreTasksAction={loadMoreTasksAction} page={page} setPage={setPage} />
        </Spin>
        ) }
      </Contents>

      <ModalAction
        t={t}
        selectedTasks={items.filter((item) => item.selected)}
        isModalVisible={isModalActionVisible}
        setIsModalVisible={handleModalActionVisible}
        setPage={setPage}
      />
    </>
  )
}

TaskScreen.propTypes = {
  items: PropTypes.array,
  setItems: PropTypes.func,
  isSelectedAll: PropTypes.bool,
  isModalActionVisible: PropTypes.bool,
  setIsModalActionVisible: PropTypes.func
}

export default TaskScreen
